import { useEffect, useState } from 'react'
import { Button } from 'app/components/ui/button'
import { Dialog, DialogActionBar, DialogContent, DialogTitleBar } from 'app/components/ui/dialog'
import { Input } from 'app/components/ui/input'
import { pruneFallbacks, TemplateFallbacksEditor } from 'app/components/templates/TemplateFallbacksEditor'
import type { SmsTemplate } from 'app/generated/graphql'
import { toEngineText } from 'app/lib/messageTemplates'
import { readFallbacks, writeFallbacks } from 'app/lib/workflows/messageFallbacks'
import type { SmsTemplateInput } from 'app/stores/SmsTemplateStore'
import { TemplateBodyEditor } from './TemplateBodyEditor'

type TemplateEditorDialogProps = {
  open: boolean
  /** Template being edited; undefined = creating a new one. */
  template?: SmsTemplate
  /** Pre-filled body when capturing from a composer ("save current message"). */
  initialBody?: string
  saving?: boolean
  onSave: (input: SmsTemplateInput) => void | Promise<void>
  onCancel: () => void
}

/** Create/edit dialog for an SMS template, used by the settings page and by
 *  the picker's "save current message" capture on every compose surface. */
export function TemplateEditorDialog({
  open,
  template,
  initialBody,
  saving,
  onSave,
  onCancel,
}: TemplateEditorDialogProps) {
  const [name, setName] = useState(``)
  const [body, setBody] = useState(``)
  const [fallbacks, setFallbacks] = useState<Record<string, string>>({})

  // Re-seed the form each time the dialog opens (for a different template or a
  // fresh capture). Keyed on template?.id, NOT the object reference. The store
  // re-parses JSON on every fetchList, so a background refresh would produce a
  // new reference for unchanged content and silently wipe in-progress edits.
  useEffect(() => {
    if (!open) return
    setName(template?.name ?? ``)
    // Fallbacks are stored inside the text; the dialog edits them in their own inputs.
    const stored = template?.body ?? initialBody ?? ``
    setBody(writeFallbacks(stored, {}))
    setFallbacks(readFallbacks(stored))
  }, [open, template?.id, initialBody])

  const canSave = !!name.trim() && !!body.trim() && !saving

  const prunedFallbacks = () => pruneFallbacks(fallbacks, body)

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      {/* max-h with the body scrolling between the bars: with fallback rows this dialog can
          outgrow a phone viewport, and the footer buttons must stay reachable. */}
      <DialogContent
        showClose={false}
        className="flex max-h-[90vh] max-w-2xl flex-col gap-0 overflow-hidden rounded-xl p-0"
      >
        <DialogTitleBar title={template ? `Edit template` : `New template`} />
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="sms-template-name" className="text-ui-sm font-medium text-default">
              Name
            </label>
            <Input
              id="sms-template-name"
              autoFocus
              value={name}
              placeholder="e.g. Running late"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="sms-template-body" className="text-ui-sm font-medium text-default">
              Message
            </label>
            <TemplateBodyEditor id="sms-template-body" value={body} onChange={setBody} rows={8} />
          </div>
          <TemplateFallbacksEditor tokenedText={body} value={fallbacks} onChange={setFallbacks} />
        </div>
        <DialogActionBar>
          <Button variant="secondary" size="md" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            disabled={!canSave}
            onClick={() => onSave({ name: name.trim(), body: toEngineText(body, prunedFallbacks()) })}
          >
            {saving ? `Saving…` : `Save template`}
          </Button>
        </DialogActionBar>
      </DialogContent>
    </Dialog>
  )
}
